import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IndividualFormDialog = ({ open, initial, classes, headers, onCancel, onSubmit }) => {
  const [form, setForm] = useState({
    individual_id: initial.individual_id,
    name: initial.name || '',
    class_id: initial.class_id || '',
    description: initial.description || '',
    properties: initial.properties || {},
    linked_note_ids: initial.linked_note_ids || [],
  });
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/pkm/notes?limit=100`, { headers }); setNotes(r.data || []); } catch { /* */ }
    })();
  }, [headers]);

  const cls = classes.find(c => c.class_id === form.class_id);
  const classProps = cls?.properties || [];

  const toggleNote = (noteId, checked) => {
    setForm({
      ...form,
      linked_note_ids: checked ? [...form.linked_note_ids, noteId] : form.linked_note_ids.filter(x => x !== noteId),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{form.individual_id ? 'Edit Individual' : 'New Individual'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="individual-name-input" />
            </div>
            <div>
              <Label className="text-xs">Class *</Label>
              <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select a class" /></SelectTrigger>
                <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>

          {classProps.length > 0 && (
            <div>
              <Label className="text-xs">Class properties</Label>
              <div className="space-y-1.5 mt-1">
                {classProps.map(p => (
                  <div key={p.name} className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-xs font-mono text-[#002FA7]">{p.name}:{p.datatype}{p.required && '*'}</span>
                    <Input className="h-8 text-xs" value={form.properties[p.name] || ''} onChange={(e) => setForm({ ...form, properties: { ...form.properties, [p.name]: e.target.value } })} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Link to PKM notes ({form.linked_note_ids.length})</Label>
            <div className="max-h-[140px] overflow-y-auto border border-[#E5E7EB] rounded p-1 space-y-0.5 mt-1">
              {notes.slice(0, 50).map(n => (
                <label key={n.note_id} className="flex items-start gap-1.5 p-1 text-xs cursor-pointer hover:bg-[#F9FAFB]">
                  <input type="checkbox" checked={form.linked_note_ids.includes(n.note_id)} onChange={(e) => toggleNote(n.note_id, e.target.checked)} />
                  <span className="truncate">{n.title}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button className="bg-[#002FA7] hover:bg-[#001F6F]" onClick={() => onSubmit(form)} disabled={!form.name.trim() || !form.class_id} data-testid="submit-individual">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IndividualFormDialog;
